import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { useGetCoverageFunction } from '@ais-ui/api';
import { DateRange } from 'react-day-picker';
import { transform } from 'ol/proj';
import { useToast } from '@ais-ui/ui-core';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { AddLayer, shapeToVectorLayer } from '@ais-ui/react-openlayers';
import { IntervalItem, LegendConfig } from '@ais-ui/websockets';

interface CoverageContextProps {
  children: ReactNode;
}

interface CoverageContextValue {
  selectedSourceIdentifiers: SourceIdentifier[];
  setSelectedSourceIdentifiers: Dispatch<SetStateAction<SourceIdentifier[]>>;
  onSourceIdentifierSelect: (sourceIdentifier: SourceIdentifier) => void;
  coverageDate: DateRange | undefined;
  setCoverageDate: Dispatch<SetStateAction<DateRange | undefined>>;
  coverageOverlays: Record<
    number,
    {
      base64: string;
      imageExtent: number[];
    }
  >;
  addSourceIdentifiersToMap: (
    addLayer: AddLayer,
    sourceIdentifiers: SourceIdentifier[]
  ) => void;
  coverageLegend: LegendConfig<'interval'>;
  showCoverageLegend: boolean;
}

// Helper function to parse WKT Point
const parsePoint = (wkt: string): [number, number] => {
  // Expected format: 'POINT (longitude latitude)'
  const match = wkt.match(
    /^POINT\s*\(\s*(-?\d+(\.\d+)?)\s+(-?\d+(\.\d+)?)\s*\)$/
  );

  if (!match) {
    throw new Error(`Invalid WKT Point format: "${wkt}"`);
  }

  const longitude = parseFloat(match[1]);
  const latitude = parseFloat(match[3]);

  return [longitude, latitude];
};

export const createLegend = (): LegendConfig<'interval'> => {
  const intervals = [
    { start: 0, end: 5, color: '#ff4000' },
    { start: 5, end: 10, color: '#ff6e00' },
    { start: 10, end: 15, color: '#ff9100' },
    { start: 15, end: 20, color: '#ffa600' },
    { start: 20, end: 25, color: '#ffb700' },
    { start: 30, end: 35, color: '#ffc400' },
    { start: 35, end: 40, color: '#ffd100' },
    { start: 40, end: 45, color: '#ffe600' },
    { start: 45, end: 50, color: '#ffee00' },
    { start: 50, end: 55, color: '#fff700' },
    { start: 55, end: 60, color: '#ffff00' },
    { start: 60, end: 65, color: '#f3ff00' },
    { start: 65, end: 70, color: '#eaff00' },
    { start: 70, end: 75, color: '#ddff00' },
    { start: 75, end: 80, color: '#d1ff00' },
    { start: 80, end: 85, color: '#bfff00' },
    { start: 85, end: 90, color: '#aaff00' },
    { start: 90, end: 95, color: '#88ff00' },
    { start: 95, end: 100, color: '#55ff00' },
  ];

  const items = intervals.map((interval) => ({
    color: interval.color,
    start_interval: interval.start,
    end_interval: interval.end,
    text: `${interval.start}% - ${interval.end}%`,
  }));

  const legend: LegendConfig<'interval'> = {
    type: 'interval',
    metric: 'speed',
    unit: 'percentage',
    title: 'AIS Signal Coverage',
    items: items as IntervalItem[],
  };

  return legend;
};

const CoverageContext = createContext<CoverageContextValue | undefined>(
  undefined
);

export const CoverageProvider = ({ children }: CoverageContextProps) => {
  const [selectedSourceIdentifiers, setSelectedSourceIdentifiers] = useState<
    SourceIdentifier[]
  >([]);
  const [coverageLegend] = useState<LegendConfig<'interval'>>(() =>
    createLegend()
  );
  const { toast } = useToast();
  const [coverageDate, setCoverageDate] = React.useState<DateRange | undefined>(
    {
      from: new Date(new Date().getTime() - 48 * 60 * 60 * 1000),
      to: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
    }
  );

  const [coverageOverlays, setCoverageOverlays] = useState<
    Record<
      number,
      {
        base64: string;
        imageExtent: number[];
      }
    >
  >({});

  const showCoverageLegend = useMemo(
    () => selectedSourceIdentifiers.length > 0,
    [selectedSourceIdentifiers]
  );

  const { getCoverage } = useGetCoverageFunction();

  const onSourceIdentifierSelect = useCallback(
    (sourceIdentifier: SourceIdentifier) => {
      setSelectedSourceIdentifiers((prevSelected) => {
        const exists = prevSelected.some(
          (selected) => selected.id === sourceIdentifier.id
        );
        if (exists) {
          const newSelected = prevSelected.filter(
            (selected) => selected.id !== sourceIdentifier.id
          );
          toast({
            title: 'Success!',
            description:
              'Unselected source identifier and removing its coverage from the map.',
          });
          return newSelected;
        } else {
          const newSelected = [...prevSelected, sourceIdentifier];
          toast({
            title: 'Success!',
            description:
              'Selected source identifier and adding its coverage to the map.',
          });
          return newSelected;
        }
      });
    },
    [toast]
  );

  useEffect(() => {
    const updateCoverageOverlays = async () => {
      const newOverlays: Record<
        number,
        {
          base64: string;
          imageExtent: number[];
        }
      > = {};

      for (const sourceIdentifier of selectedSourceIdentifiers) {
        if (!coverageDate) {
          continue;
        }

        try {
          const coverageResponse = await getCoverage(
            sourceIdentifier.id,
            coverageDate
          );
          const response = await coverageResponse;

          const regex = /\((-?\d+\.\d+)\s(-?\d+\.\d+)\)/g;
          const coordinates: number[] = [];
          let match;

          while ((match = regex.exec(response.viewBoxCoordinates)) !== null) {
            const [_, longitude, latitude] = match;
            // Transform coordinates from EPSG:4326 to the map's projection
            const [x, y] = transform(
              [Number(longitude), Number(latitude)],
              'EPSG:4326',
              'EPSG:3857'
            );
            coordinates.push(x, y);
          }

          // The resulting coordinates array needs to be flattened into an extent
          const imageExtent = [
            coordinates[0],
            coordinates[1],
            coordinates[2],
            coordinates[3],
          ];

          newOverlays[sourceIdentifier.id] = {
            base64: response.image,
            imageExtent,
          };
        } catch (error) {
          console.error(
            `Failed to fetch coverage for ${sourceIdentifier.id}`,
            error
          );
        }
      }

      // Only update if there are changes to avoid re-triggering useEffect
      setCoverageOverlays((prevOverlays) => {
        const hasChanges = Object.keys(newOverlays).some(
          (sourceIdentifierId) =>
            !prevOverlays[parseInt(sourceIdentifierId)] ||
            prevOverlays[parseInt(sourceIdentifierId)].base64 !==
              newOverlays[parseInt(sourceIdentifierId)].base64 ||
            JSON.stringify(
              prevOverlays[parseInt(sourceIdentifierId)].imageExtent
            ) !==
              JSON.stringify(
                newOverlays[parseInt(sourceIdentifierId)].imageExtent
              )
        );
        return hasChanges ? newOverlays : prevOverlays;
      });
    };

    updateCoverageOverlays();
  }, [selectedSourceIdentifiers, getCoverage, coverageDate]);

  useEffect(() => {
    const handleRemoveCoverage = (event: Event) => {
      // Type assertion to inform TypeScript that this is a CustomEvent
      const customEvent = event as CustomEvent<{ id: string }>;
      const { id } = customEvent.detail;

      setSelectedSourceIdentifiers((prevSelected) =>
        prevSelected.filter((item) => item.id !== parseInt(id))
      );
    };

    // Attach the event listener
    window.addEventListener('removeCoverage', handleRemoveCoverage);

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('removeCoverage', handleRemoveCoverage);
    };
  }, []);

  const addSourceIdentifiersToMap = (
    addLayer: AddLayer,
    sourceIdentifiers: SourceIdentifier[]
  ) => {
    if (!sourceIdentifiers || sourceIdentifiers.length === 0) return null;

    const sourceIdentifierGeoJson: GeoJson = {
      type: 'FeatureCollection',
      properties: {
        id: 'source-identifiers',
        name: 'Source Identifiers',
        icon: 'source-identifier',
      },
      features: sourceIdentifiers.map((sourceIdentifier) => {
        const coordinates = parsePoint(sourceIdentifier.pos);
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates,
          },
          properties: {
            id: sourceIdentifier.id.toString(),
            name:
              sourceIdentifier.pssMmsi === -1
                ? sourceIdentifier.sourceTag
                : sourceIdentifier.pssMmsi.toString(),
            pssMmsi: sourceIdentifier.pssMmsi,
            sourceTag: sourceIdentifier.sourceTag,
            countryMid: sourceIdentifier.countryMid,
            pos: sourceIdentifier.pos,
            regionId: sourceIdentifier.regionId,
            covEnabled: sourceIdentifier.covEnabled,
            createdTimeUtc: sourceIdentifier.createdTimeUtc,
            uuid: sourceIdentifier.uuid,
            type: 'source-identifier',
            size: 'medium',
          },
        };
      }),
    };

    const layer = shapeToVectorLayer(sourceIdentifierGeoJson);

    addLayer(
      layer,
      'Source identifiers',
      true,
      true,
      true,
      sourceIdentifierGeoJson.properties,
      sourceIdentifierGeoJson.properties.id,
      false,
      true
    );
  };

  const value = useMemo(
    () => ({
      selectedSourceIdentifiers,
      setSelectedSourceIdentifiers,
      onSourceIdentifierSelect,
      coverageDate,
      setCoverageDate,
      coverageOverlays,
      addSourceIdentifiersToMap,
      coverageLegend,
      showCoverageLegend,
    }),
    [
      selectedSourceIdentifiers,
      setSelectedSourceIdentifiers,
      onSourceIdentifierSelect,
      coverageDate,
      setCoverageDate,
      coverageOverlays,
      addSourceIdentifiersToMap,
      coverageLegend,
      showCoverageLegend,
    ]
  );

  return (
    <CoverageContext.Provider value={value}>
      {children}
    </CoverageContext.Provider>
  );
};

export const useCoverage = () => {
  const context = useContext(CoverageContext);
  if (!context) {
    throw new Error(
      'useCoverage must be used within a CoverageContextProvider'
    );
  }
  return context;
};
